import { Button, Col, Flex, Form, FormInstance, Row, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';

// components
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import SwitchElement from 'components/switch/switch';

// services

// styles
import styles from '../boards.module.scss';

// models

interface BoardSettingsComponentProps {
	form: FormInstance;
	viewOnly?: boolean;
}

const BoardSettingsComponent: FunctionComponent<BoardSettingsComponentProps> = ({ form, viewOnly }) => {
	const { t } = useTranslation();

	const [maxAbsenceBeforeNotification, setMaxAbsenceBeforeNotification] = useState(
		viewOnly && !!(form.getFieldValue('maximum_absence_before_notification') != null),
	);
	const [maximumIndependentMembersAvailable, setMaximumIndependentMembersAvailable] = useState(
		viewOnly && !!(form.getFieldValue('maximum_independent_members') != null),
	);
	const [maximumExecutiveMembersAvailable, setMaximumExecutiveMembersAvailable] = useState(
		viewOnly && !!(form.getFieldValue('maximum_executive_members') != null),
	);

	return (
		<Row gutter={[16, 16]}>
			<Col span={24} md={11}>
				<div>
					<h6 className="text-primary fw-medium mb-1">{t('ACTIONS.BOARD_GOVERNANCE')}</h6>
					<p className="text-light">{t('ACTIONS.PLEASE_CHOOSE_BOARD_GOVERNANCE')}</p>
					<main className={styles.boardItem_details}>
						<Flex align="center" gap={10}>
							<p>{t('ACTIONS.TOTAL_BOARD_MEMBERS')}</p>
							<FormNumberElement
								name="total_board_members"
								inputStyles={{ width: '140px' }}
								formClasses="mb-0"
								inputClasses="text-center"
								size="middle"
								disabled={viewOnly}
								rules={[
									{ required: true, message: t('COMMON.FIELD_REQUIRED') },
									{ min: 3, max: 15, type: 'number', message: t('ACTIONS.TOTAL_BOARD_MEMBERS_VALIDATION') },
								]}
								addonAfter={<span>{t('ACTIONS.MEMBERS')}</span>}
							/>
						</Flex>
						<Flex align="center" gap={10}>
							<p>{t('ACTIONS.BOARD_OF_DIRECTORS_TERM')}</p>
							<FormNumberElement
								name="board_term_years"
								inputStyles={{ width: '120px' }}
								formClasses="mb-0"
								inputClasses="text-center"
								disabled={viewOnly}
								size="middle"
								rules={[
									{ required: true, message: t('COMMON.FIELD_REQUIRED') },
									{ min: 1, type: 'number', message: t('ACTIONS.BOARD_OF_DIRECTORS_TERM_VALIDATION') },
								]}
								addonAfter={<span>{t('ACTIONS.YEARS')}</span>}
							/>
						</Flex>
						<Flex align="center" gap={10}>
							<p>{t('ACTIONS.MINIMUM_QUORUM_OF_MEETINGS')}</p>
							<FormNumberElement
								name="minimum_quorum_percentage"
								inputStyles={{ width: '90px' }}
								formClasses="mb-0"
								inputClasses="text-center"
								size="middle"
								disabled={viewOnly}
								min={0}
								max={100}
								addonAfter={<span>%</span>}
								rules={[
									{ required: true, message: t('COMMON.FIELD_REQUIRED') },
									{ min: 50, max: 100, type: 'number', message: t('ACTIONS.MINIMUM_QUORUM_OF_MEETINGS_VALIDATION') },
								]}
							/>
						</Flex>
						<Flex align="center" gap={10}>
							<Switch
								defaultChecked={viewOnly && maximumExecutiveMembersAvailable}
								disabled={viewOnly}
								onChange={(value) => {
									setMaximumExecutiveMembersAvailable(value);
									if (value) form.setFieldValue('maximum_executive_members', 0);
									else form.setFieldValue('maximum_executive_members', null);
								}}
							/>
							<p>{t('ACTIONS.MAXIMUM_EXECUTIVE_MEMBERS')}</p>
							<FormNumberElement
								name="maximum_executive_members"
								inputStyles={{ width: '140px' }}
								formClasses="mb-0"
								inputClasses="text-center"
								size="middle"
								rules={[{ required: maximumExecutiveMembersAvailable, message: t('COMMON.FIELD_REQUIRED') }]}
								addonAfter={<span>{t('ACTIONS.MEMBERS')}</span>}
								disabled={!maximumExecutiveMembersAvailable || viewOnly}
							/>
						</Flex>
						<Flex align="center" gap={10}>
							<Switch
								defaultChecked={viewOnly && maximumIndependentMembersAvailable}
								disabled={viewOnly}
								onChange={(value) => {
									setMaximumIndependentMembersAvailable(value);
									if (value) form.setFieldValue('maximum_independent_members', 0);
									else form.setFieldValue('maximum_independent_members', null);
								}}
							/>
							<p>{t('ACTIONS.MAXIMUM_INDEPENDENT_MEMBERS')}</p>
							<FormNumberElement
								name="maximum_independent_members"
								inputStyles={{ width: '140px' }}
								formClasses="mb-0"
								inputClasses="text-center"
								size="middle"
								rules={[{ required: maximumIndependentMembersAvailable, message: t('COMMON.FIELD_REQUIRED') }]}
								addonAfter={<span>{t('ACTIONS.MEMBERS')}</span>}
								disabled={!maximumIndependentMembersAvailable || viewOnly}
							/>
						</Flex>
					</main>
				</div>
			</Col>

			<Col span={0} md={1}>
				<div style={{ width: 2, height: '100%', backgroundColor: '#E4E7EC', margin: 'auto' }}></div>
			</Col>

			<Col span={24} md={12}>
				<h6 className="text-primary fw-medium mb-1">{t('ACTIONS.BOARD_SETTINGS')}</h6>
				<p className="text-light">{t('ACTIONS.OPTIONAL_SETTINGS_TO_ENHANCE_BOARD_PERFORMANCE')}</p>
				<main className={styles.boardItem_details}>
					<Flex align="center" gap={10}>
						<p>{t('ACTIONS.MINIMUM_NOTIF_BEFORE_MEETINGS')}</p>
						<FormNumberElement
							name="minimum_notification_days_before_meetings"
							inputStyles={{ width: '110px' }}
							formClasses="mb-0"
							inputClasses="text-center mb-0"
							disabled={viewOnly}
							size="middle"
							addonAfter={<span>{t('ACTIONS.DAYS')}</span>}
							rules={[
								{ required: true, message: t('COMMON.FIELD_REQUIRED') },
								{ min: 1, type: 'number', message: t('ACTIONS.MINIMUM_NOTIF_BEFORE_MEETINGS_VALIDATION') },
							]}
						/>
					</Flex>

					<Flex align="center" gap={10}>
						<Switch
							value={maxAbsenceBeforeNotification}
							onChange={(value) => {
								setMaxAbsenceBeforeNotification(value);
								if (value) form.setFieldValue('maximum_absence_before_notification', 1);
								else form.setFieldValue('maximum_absence_before_notification', null);
							}}
							disabled={viewOnly}
						/>
						<p>{t('ACTIONS.MAXIMUM_ABSENCE_BEFORE_NOTIF')}</p>
						<FormNumberElement
							disabled={!maxAbsenceBeforeNotification || viewOnly}
							name="maximum_absence_before_notification"
							inputStyles={{ width: '140px' }}
							formClasses="mb-0"
							inputClasses="text-center"
							size="middle"
							min={1}
							addonAfter={<span>{t('ACTIONS.MEETINGS')}</span>}
							rules={[
								{ required: maxAbsenceBeforeNotification, message: t('COMMON.FIELD_REQUIRED') },
								{ min: 0, max: 50, type: 'number', message: t('ACTIONS.MAXIMUM_ABSENCE_BEFORE_NOTIF_VALIDATION') },
							]}
						/>
					</Flex>

					<Flex align="center" gap={10} className="mb-4">
						<SwitchElement
							label={t('ACTIONS.ALLOW_SECRETARY_UPDATE_WHILE_BOARD_IS_ACTIVE')}
							onChange={(value) => form.setFieldValue('allow_secretary_update_while_active', value)}
							checked={viewOnly && form.getFieldValue('allow_secretary_update_while_active')}
							disabled={viewOnly}
						/>
						<Form.Item name="allow_secretary_update_while_active" hidden noStyle />
					</Flex>

					<Flex align="center" gap={10} className="mb-4">
						<SwitchElement
							disabled={viewOnly}
							label={t('ACTIONS.ALLOW_SWIPE_VOTING')}
							checked={viewOnly && form.getFieldValue('allow_swipe_voting')}
							onChange={(value) => form.setFieldValue('allow_swipe_voting', value)}
						/>
						<Form.Item name="allow_swipe_voting" hidden noStyle />
					</Flex>

					<Flex align="center" gap={10}>
						<SwitchElement
							label={t('ACTIONS.ALLOW_EXTERNAL_GUEST_INVITATION')}
							checked={viewOnly && form.getFieldValue('allow_external_guest_invitation')}
							onChange={(value) => form.setFieldValue('allow_external_guest_invitation', value)}
							disabled={viewOnly}
						/>
						<Form.Item name="allow_external_guest_invitation" hidden noStyle />
					</Flex>
				</main>
			</Col>
		</Row>
	);
};

export default BoardSettingsComponent;
